<template>
  <div class="bg-[#144A78] text-white shadow-md z-10">
    <!-- Top Bar with Banner -->
    <div class="bg-white relative">
      <div class="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8">
        <img
          :src="getLocalizedImage"
          alt="Header Banner"
          class="w-full max-w-[1200px] h-auto max-h-[100px] md:max-h-[170px] object-contain mx-auto"
        />
      </div>
    </div>
    
    <!-- Title Bar with Integrated Language Selector -->
    <div class="bg-white border-t border-b border-gray-200">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center gap-4">
        <div class="flex-grow w-full overflow-hidden">
          <div class="marquee-wrapper">
            <h1 class="marquee-content text-xl md:text-2xl font-bold bg-gradient-to-r from-blue-500 to-blue-700 bg-clip-text text-transparent py-2">
              {{ $t('auth') }}
            </h1>
          </div>
        </div>
        
        <div class="relative flex-shrink-0">
          <button
            @click="toggleDropdown('language')"
            class="flex items-center space-x-2 bg-gray-100 text-blue-800 px-3 py-2 rounded-md text-sm font-semibold transition duration-300 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <img 
              :src="flagSrc(currentLanguageFlag)" 
              :alt="currentLanguage" 
              class="w-5 h-auto"
            />
            <span>{{ currentLanguage }}</span>
            <i class="fas fa-chevron-down w-4 h-4 transition-transform text-gray-500" :class="{'rotate-180': dropdown === 'language'}"></i>
          </button>
          <transition name="dropdown-fade">
            <div
              v-show="dropdown === 'language'"
              class="absolute right-0 mt-2 w-40 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 z-20 origin-top-right"
            >
              <a 
                href="#" 
                @click.prevent="selectLanguage('EN')" 
                class="flex items-center px-4 py-2 text-sm transition-colors"
                :class="currentLanguage === 'EN' ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-700 hover:bg-gray-100'"
              >
                <img :src="flagSrc('gb')" alt="English" class="w-5 h-auto mr-3" />
                <span>English</span>
                <i v-if="currentLanguage === 'EN'" class="fas fa-check ml-auto text-blue-600"></i>
              </a>
              <a 
                href="#" 
                @click.prevent="selectLanguage('FR')" 
                class="flex items-center px-4 py-2 text-sm transition-colors"
                :class="currentLanguage === 'FR' ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-700 hover:bg-gray-100'"
              >
                <img :src="flagSrc('fr')" alt="French" class="w-5 h-auto mr-3" />
                <span>Français</span>
                <i v-if="currentLanguage === 'FR'" class="fas fa-check ml-auto text-blue-600"></i>
              </a>
            </div>
          </transition>
        </div>
      </div>
    </div>
    
    <!-- Main Navigation Bar -->
    <div class="bg-[#144A78]">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between items-center h-16">
          <!-- Desktop Navigation Links -->
          <nav class="hidden md:flex items-center space-x-1 xl:space-x-2 text-2xl md:text-5xl xl:text-80xl font-bolder tracking-wide">
            <router-link :to="{ name: 'home', params: { lang: $i18n.locale } }" class="nav-link">{{ $t('home') }}</router-link>
            <button @click="navigateToCardPage('public')" class="nav-link">{{ $t('publicSector') }}</button>
            <button v-if="privateSectors.length" @click="navigateToCardPage('private')" class="nav-link">{{ $t('privateSector') }}</button>
            <router-link :to="{ name: 'contact', params: { lang: $i18n.locale } }" class="nav-link">{{ $t('contactUs') }}</router-link>
            <router-link :to="`/${$i18n.locale}/register`" class="nav-link nav-link-register">
              <i class="fas fa-building mr-1"></i>{{ $t('addStructure') }}
            </router-link>
          </nav>  

          <!-- Search and Report Buttons (Desktop) -->
          <div class="hidden md:flex items-center space-x-4 justify-end flex-1">
            <div v-if="shouldShowContent" class="search-bar" :class="{ 'search-bar-open': showSuggestions && filteredSuggestions.length }" ref="searchBarDesktop">
              <form @submit.prevent="handleSearchSubmit" class="w-full">
                <input
                  v-model="searchQuery"
                  class="search-input"
                  :class="{ 'search-input-open': showSuggestions && filteredSuggestions.length }"
                  :placeholder="$t('personalities.searchPlaceholder')"
                  @input="onSearchInput"
                  @focus="onSearchFocus"
                  @keydown.down.prevent="navigateSuggestion(1)"
                  @keydown.up.prevent="navigateSuggestion(-1)"
                  @keydown.enter.prevent="handleEnter"
                  @keydown.escape="closeSuggestions"
                  autocomplete="off"
                />
                <button type="submit" class="search-button"><i class="fas fa-search"></i></button>
              </form>

              <!-- Desktop Suggestions Dropdown -->
              <div v-if="showSuggestions && (filteredSuggestions.length || isSearching)" class="suggestions-dropdown">
                <div v-if="isSearching" class="suggestion-loading">
                  <i class="fas fa-spinner fa-spin mr-2"></i>
                  <span>{{ $t('searching') || 'Searching...' }}</span>
                </div>
                <template v-else>
                  <button
                    v-for="(item, index) in filteredSuggestions"
                    :key="`${item.type}-${item.id}`"
                    class="suggestion-item"
                    :class="{ 'suggestion-active': index === activeSuggestionIndex }"
                    @mousedown.prevent="selectSuggestion(item)"
                    @mouseenter="activeSuggestionIndex = index"
                  >
                    <i :class="getSuggestionIcon(item)" class="suggestion-icon"></i>
                    <div class="suggestion-text">
                      <span class="suggestion-name" v-html="highlightMatch(item.name)"></span>
                      <span v-if="isCleanValue(item.abbreviation)" class="suggestion-separator">—</span>
                      <span v-if="isCleanValue(item.abbreviation)" class="suggestion-abbr">{{ item.abbreviation }}</span>
                    </div>
                    <i class="fas fa-arrow-up-left suggestion-arrow"></i>
                  </button>
                  <button
                    v-if="searchQuery.trim().length >= 2"
                    class="suggestion-item suggestion-see-all"
                    :class="{ 'suggestion-active': activeSuggestionIndex === filteredSuggestions.length }"
                    @mousedown.prevent="handleSearchSubmit"
                    @mouseenter="activeSuggestionIndex = filteredSuggestions.length"
                  >
                    <i class="fas fa-search suggestion-icon"></i>
                    <span class="suggestion-name">{{ $t('seeAllResults') || 'See all results for' }} "<strong>{{ searchQuery.trim() }}</strong>"</span>
                  </button>
                </template>
              </div>
            </div>
            
            <button @click="openIncidentReport" class="report-button">
              {{ $t('reportIncident') }}
            </button>
          </div>

          <!-- Mobile Hamburger — extreme LEFT -->
          <div class="md:hidden flex items-center flex-shrink-0 mr-2">
            <button @click="toggleMobileMenu" class="text-white focus:outline-none">
              <i class="fas fa-bars h-6 w-6"></i>
            </button>
          </div>

          <!-- Mobile Search Bar — fills all remaining space -->
          <div class="md:hidden flex items-center flex-1 min-w-0">
            <div v-if="shouldShowContent" class="search-bar-mobile w-full" :class="{ 'search-bar-open': showSuggestions && filteredSuggestions.length }" ref="searchBarMobile">              <form @submit.prevent="handleSearchSubmit" class="w-full">
                <input
                  v-model="searchQuery"
                  class="search-input"
                  :class="{ 'search-input-open': showSuggestions && filteredSuggestions.length }"
                  :placeholder="$t('searchPlaceholderShort')"
                  @input="onSearchInput"
                  @focus="onSearchFocus"
                  @keydown.down.prevent="navigateSuggestion(1)"
                  @keydown.up.prevent="navigateSuggestion(-1)"
                  @keydown.enter.prevent="handleEnter"
                  @keydown.escape="closeSuggestions"
                  autocomplete="off"
                />
                <button type="submit" class="search-button"><i class="fas fa-search"></i></button>
              </form>

              <!-- Mobile Suggestions Dropdown -->
              <div v-if="showSuggestions && (filteredSuggestions.length || isSearching)" class="suggestions-dropdown suggestions-dropdown-mobile">
                <div v-if="isSearching" class="suggestion-loading">
                  <i class="fas fa-spinner fa-spin mr-2"></i>
                  <span>{{ $t('searching') || 'Searching...' }}</span>
                </div>
                <template v-else>
                  <button
                    v-for="(item, index) in filteredSuggestions"
                    :key="`mobile-${item.type}-${item.id}`"
                    class="suggestion-item"
                    :class="{ 'suggestion-active': index === activeSuggestionIndex }"
                    @mousedown.prevent="selectSuggestion(item)"
                    @mouseenter="activeSuggestionIndex = index"
                  >
                    <i :class="getSuggestionIcon(item)" class="suggestion-icon"></i>
                    <div class="suggestion-text">
                      <span class="suggestion-name" v-html="highlightMatch(item.name)"></span>
                      <span v-if="isCleanValue(item.abbreviation)" class="suggestion-separator">—</span>
                      <span v-if="isCleanValue(item.abbreviation)" class="suggestion-abbr">{{ item.abbreviation }}</span>
                    </div>
                  </button>
                  <button
                    v-if="searchQuery.trim().length >= 2"
                    class="suggestion-item suggestion-see-all"
                    @mousedown.prevent="handleSearchSubmit"
                  >
                    <i class="fas fa-search suggestion-icon"></i>
                    <span class="suggestion-name">{{ $t('seeAllResults') || 'All results' }} "{{ searchQuery.trim() }}"</span>
                  </button>
                </template>
              </div>
            </div>
          </div>


        </div>
      </div>
    </div>

    <!-- Mobile Menu Popup (centered, page visible behind) -->
    <div v-show="isMobileMenuOpen" class="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm z-50 md:hidden flex items-center justify-center" @click="toggleMobileMenu">
      <div class="bg-[#144A78] rounded-2xl w-11/12 max-w-sm p-6 shadow-2xl" @click.stop>
        <!-- Close button -->
        <div class="flex justify-end mb-4">
          <button @click="toggleMobileMenu" class="text-white/60 hover:text-white">
            <i class="fas fa-times text-xl"></i>
          </button>
        </div>
        <nav class="flex flex-col space-y-4">
          <router-link :to="{ name: 'home', params: { lang: $i18n.locale } }" class="mobile-nav-link" @click="toggleMobileMenu">{{ $t('home') }}</router-link>
          <button @click="navigateToCardPage('public')" class="mobile-nav-link text-left">{{ $t('publicSector') }}</button>
          <button v-if="privateSectors.length" @click="navigateToCardPage('private')" class="mobile-nav-link text-left">{{ $t('privateSector') }}</button>
          <router-link :to="{ name: 'contact', params: { lang: $i18n.locale } }" class="mobile-nav-link" @click="toggleMobileMenu">{{ $t('contactUs') }}</router-link>
          <router-link :to="`/${$i18n.locale}/register`" class="mobile-nav-link mobile-nav-link-register" @click="toggleMobileMenu">
            <i class="fas fa-building mr-1"></i>{{ $t('addStructure') }}
          </router-link>
          <hr class="border-gray-500 my-2"/>
          <button @click="openIncidentReport" class="report-button mt-4">{{ $t('reportIncident') }}</button>
        </nav>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { escapeHtml } from '@/utils/sanitize';

const API_BASE_URL = process.env.VUE_APP_API_BASE_URL;

const FLAG_DATA = {
  gb: require('@/assets/flags/gb.svg'),
  fr: require('@/assets/flags/fr.svg'),
};

export default {
  name: "HeaderComponent",
  data() {
    return {
      publicSectors: [],
      privateSectors: [{ id: 1, value: "All Private Structures" }],
      dropdown: null,
      isMobileMenuOpen: false,
      searchQuery: '',
      previousRoute: null,
      // Autocomplete state
      suggestions: [],
      showSuggestions: false,
      isSearching: false,
      activeSuggestionIndex: -1,
      debounceTimer: null,
      abortController: null,
    };
  },
  computed: {
    currentLanguage() {
      return this.$i18n.locale.toUpperCase();
    },
    currentLanguageFlag() {
      return this.$i18n.locale === 'fr' ? 'fr' : 'gb';
    },
    filteredSuggestions() {
      return this.suggestions.filter(item => {
        // Remove items with no usable name
        if (!item.name || !item.name.trim()) return false;
        return true;
      });
    },
    shouldShowContent() {
      const excludedNames = ['home', 'welcome', 'SearchResults'];
      const excludedPaths = ['/', '/search-results'];
      const routeName = this.$route.name;
      const routePath = this.$route.path.substring(3);
      return !excludedNames.some(name => routeName && routeName.toLowerCase().includes(name.toLowerCase())) && 
             !excludedPaths.includes(routePath) && routePath !== '';
    },
    getLocalizedImage() {
      const language = this.$i18n.locale || 'en';
      try {
        const isMobile = window.innerWidth < 768;
        const imageName = isMobile ? `banner-${language}` : `antic-${language}`;
        return require(`@/assets/images/${imageName}.png`);
      } catch (e) {
        return require(`@/assets/images/antic-en.png`);
      }
    },
  },
  watch: {
    '$route'(to, from) {
      if (to.name !== 'search-results') {
        this.previousRoute = from;
      }
      this.closeSuggestions();
    },
  },
  methods: {
    // =================== AUTOCOMPLETE ===================

    onSearchInput() {
      const query = this.searchQuery.trim();

      if (query.length < 2) {
        this.suggestions = [];
        this.showSuggestions = false;
        this.activeSuggestionIndex = -1;
        return;
      }

      this.activeSuggestionIndex = -1;

      // Debounce: wait 300ms after user stops typing
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.fetchSuggestions(query);
      }, 300);
    },

    onSearchFocus() {
      if (this.suggestions.length && this.searchQuery.trim().length >= 2) {
        this.showSuggestions = true;
      }
    },

    async fetchSuggestions(query) {
      // Cancel any in-flight request to avoid stale results
      if (this.abortController) {
        this.abortController.abort();
      }
      this.abortController = new AbortController();

      this.isSearching = true;
      this.showSuggestions = true;

      const currentLang = this.$i18n.locale;
      const altLang = currentLang === 'fr' ? 'en' : 'fr';

      try {
        // Fire BOTH language requests in parallel for bilingual coverage
        // "ministere de finance" typed in English UI → EN returns junk, FR returns the real match
        const [primaryRes, altRes] = await Promise.allSettled([
          axios.get(`${API_BASE_URL}/search/autocomplete/`, {
            params: { q: query, limit: 6 },
            headers: { 'Accept-Language': currentLang },
            signal: this.abortController.signal,
          }),
          axios.get(`${API_BASE_URL}/search/autocomplete/`, {
            params: { q: query, limit: 6 },
            headers: { 'Accept-Language': altLang },
            signal: this.abortController.signal,
          }),
        ]);

        // Bail if user has already typed something else
        if (this.searchQuery.trim() !== query) return;

        const primaryItems = primaryRes.status === 'fulfilled'
          ? (primaryRes.value.data.suggestions || [])
          : [];
        const altItems = altRes.status === 'fulfilled'
          ? (altRes.value.data.suggestions || [])
          : [];

        // Merge: primary language first, then alternate, deduplicated by id+type
        const seen = new Set();
        const merged = [];

        for (const item of [...primaryItems, ...altItems]) {
          const key = `${item.type}-${item.id}`;
          if (!seen.has(key)) {
            seen.add(key);
            merged.push(item);
          }
        }

        this.suggestions = merged.slice(0, 8); // cap at 8 items
        this.isSearching = false;

      } catch (err) {
        // Ignore AbortError (caused by cancelling a stale request)
        if (!axios.isCancel(err) && err.name !== 'AbortError') {
          this.suggestions = [];
          this.isSearching = false;
        }
      }
    },

    /**
     * Returns true if a value is usable for display (not null, empty, NaN, None, etc.)
     */
    isCleanValue(val) {
      if (val === null || val === undefined) return false;
      const s = String(val).toLowerCase().trim();
      return s !== '' && s !== 'nan' && s !== 'none' && s !== 'null' && s !== 'undefined';
    },

    /**
     * When a user clicks a suggestion, navigate to the SearchResults page
     * passing the selected item's name as the search query.
     * The SearchResults page handles all store setup, data fetching,
     * and proper navigation to detail pages.
     */
    selectSuggestion(item) {
      this.closeSuggestions();

      // Use abbreviation if available and clean, else full name
      const searchTerm = (this.isCleanValue(item.abbreviation)) ? item.abbreviation : item.name;
      this.searchQuery = '';

      this.$router.push({
        name: 'search-results',
        params: { lang: this.$i18n.locale },
        query: { q: searchTerm },
      }).catch(() => {});
    },

    navigateSuggestion(direction) {
      if (!this.showSuggestions) return;

      const maxIndex = this.filteredSuggestions.length; // includes "see all" row
      let newIndex = this.activeSuggestionIndex + direction;

      if (newIndex < -1) newIndex = maxIndex;
      if (newIndex > maxIndex) newIndex = -1;

      this.activeSuggestionIndex = newIndex;
    },

    handleEnter() {
      if (this.activeSuggestionIndex >= 0 && this.activeSuggestionIndex < this.filteredSuggestions.length) {
        this.selectSuggestion(this.filteredSuggestions[this.activeSuggestionIndex]);
      } else {
        this.handleSearchSubmit();
      }
    },

    closeSuggestions() {
      this.showSuggestions = false;
      this.activeSuggestionIndex = -1;
    },

    getSuggestionIcon(item) {
      // Use the icon field from the backend autocomplete response
      const iconMap = {
        'building':        'fas fa-building',
        'globe':           'fas fa-globe',
        'user-tie':        'fas fa-user-tie',
        'users':           'fas fa-users',
        'star':            'fas fa-star',
        'shield':          'fas fa-shield-alt',
        'heart-pulse':     'fas fa-heartbeat',
        'landmark':        'fas fa-landmark',
        'graduation-cap':  'fas fa-graduation-cap',
        'briefcase':       'fas fa-briefcase',
        'file':            'fas fa-file',
      };

      // Primary: use the icon field returned by the API
      if (item.icon && iconMap[item.icon]) {
        return iconMap[item.icon];
      }

      // Fallback: derive from type string for backward compatibility
      const typeLower = (item.type || '').toLowerCase();
      const typeFallback = {
        'structure':        'fas fa-building',
        'embassy':          'fas fa-globe',
        'boss':             'fas fa-user-tie',
        'statepersonnels':  'fas fa-users',
        'publicpersonality':'fas fa-star',
        'ambassador':       'fas fa-globe',
        'policestation':    'fas fa-shield-alt',
        'hospital':         'fas fa-heartbeat',
        'bank':             'fas fa-landmark',
        'creditunion':      'fas fa-landmark',
        'school':           'fas fa-graduation-cap',
        'enterprise':       'fas fa-briefcase',
      };

      return typeFallback[typeLower] || 'fas fa-building';
    },

    highlightMatch(text) {
      if (!text || !this.searchQuery.trim()) return escapeHtml(text);
      const safe = escapeHtml(text);
      const query = escapeHtml(this.searchQuery.trim()).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(${query})`, 'gi');
      return safe.replace(regex, '<mark class="highlight-match">$1</mark>');
    },

    flagSrc(code) {
      return FLAG_DATA[code] || '';
    },

    // =================== EXISTING METHODS ===================

    handleSearchSubmit() {
      this.closeSuggestions();
      if (this.searchQuery.trim()) {
        this.$router.push({
          name: 'search-results',
          params: { lang: this.$i18n.locale },
          query: { q: this.searchQuery.trim() },
        });
        this.searchQuery = '';
      }
    },
    openIncidentReport() {
      if (this.isMobileMenuOpen) {
        this.toggleMobileMenu();
      }
      window.open('https://incident.cirt.cm/en/report-incident', '_blank', 'noopener,noreferrer');
    },
    handleInput() {
      // Kept for backward compat — autocomplete now handled by onSearchInput
    },
    toggleDropdown(dropdownName) {
      this.dropdown = this.dropdown === dropdownName ? null : dropdownName;
    },
    selectLanguage(language) {
      const langCode = language.toLowerCase();
      this.$i18n.locale = langCode;
      localStorage.setItem('user-language', langCode);
      this.$router.push({
        name: this.$route.name,
        params: { ...this.$route.params, lang: langCode },
        query: this.$route.query,
      }).catch(() => {});
      this.dropdown = null;
    },
    toggleMobileMenu() {
      this.isMobileMenuOpen = !this.isMobileMenuOpen;
    },
    handleClickOutside(event) {
      if (this.$el && !this.$el.contains(event.target)) {
        this.dropdown = null;
        this.isMobileMenuOpen = false;
        this.closeSuggestions();
      }
      // Also close suggestions if click is outside search bars
      const desktopBar = this.$refs.searchBarDesktop;
      const mobileBar = this.$refs.searchBarMobile;
      const clickedInSearch =
        (desktopBar && desktopBar.contains(event.target)) ||
        (mobileBar && mobileBar.contains(event.target));
      if (!clickedInSearch) {
        this.closeSuggestions();
      }
    },
    navigateToCardPage(sectorType) {
      this.$router.push({ 
        name: 'categories', 
        params: { lang: this.$i18n.locale, sectorType: sectorType }
      });
      this.isMobileMenuOpen = false;
    },
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
    clearTimeout(this.debounceTimer);
    if (this.abortController) {
      this.abortController.abort();
    }
  },
};
</script>

<style scoped>
/* Common Link Styles */
.nav-link, .report-button, .mobile-nav-link {
  @apply px-3 py-2 rounded-md text-base font-semibold transition duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-blue-800 focus:ring-white;
}
.nav-link {
  @apply text-white hover:bg-[#1067be];
}
.nav-link-register {
  @apply bg-yellow-400 text-blue-900 hover:bg-yellow-300 rounded-md px-3 py-1.5;
}
.report-button {
  @apply bg-red-600 hover:bg-red-700 text-white whitespace-nowrap;
}
.mobile-nav-link {
  @apply block w-full px-3 py-2 rounded-md text-base font-semibold text-white hover:bg-[#1067be];
}
.mobile-nav-link-register {
  @apply bg-yellow-400 text-blue-900 hover:bg-yellow-300 mt-2;
}

/* Marquee Animation */
.marquee-wrapper {
  width: 100%;
  overflow: hidden;
  position: relative;
}
.marquee-wrapper::before {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2rem;
  z-index: 1;
  background: linear-gradient(to right, white, transparent);
}
.marquee-content {
  display: inline-block;
  white-space: nowrap;
  padding-left: 100%;
  animation: marquee 20s linear infinite;
  will-change: transform;
}
@keyframes marquee {
  0% { transform: translateX(0); }
  100% { transform: translateX(-100%); }
}
.marquee-wrapper:hover .marquee-content {
  animation-play-state: paused;
}

/* Search Bar — Google-style pill */
.search-bar {
  @apply relative w-full max-w-[280px];
}
.search-bar-mobile {
  @apply relative w-full max-w-[500px];
}
.search-input {
  width: 100%;
  height: 42px;
  padding: 0 44px 0 16px;
  font-size: 14px;
  color: #202124;
  background: #fff;
  border: 1px solid #dfe1e5;
  border-radius: 24px;
  outline: none;
  transition: box-shadow 0.2s, border-color 0.2s, border-radius 0.2s;
}
.search-input:hover {
  box-shadow: 0 1px 6px rgba(32,33,36,0.18);
  border-color: rgba(223,225,229,0);
}
.search-input:focus {
  box-shadow: 0 1px 6px rgba(32,33,36,0.18);
  border-color: rgba(223,225,229,0);
}
/* When dropdown is open: flatten bottom corners to connect */
.search-input-open {
  border-radius: 24px 24px 0 0;
  border-bottom-color: #e8eaed;
  box-shadow: 0 1px 6px rgba(32,33,36,0.18);
}
.search-button {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4285f4;
  background: none;
  border: none;
  cursor: pointer;
  border-radius: 0 24px 24px 0;
  transition: background 0.15s;
}
.search-button:hover {
  background: rgba(66, 133, 244, 0.04);
}

/* ===== GOOGLE-STYLE SUGGESTIONS DROPDOWN ===== */
.suggestions-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: #fff;
  border-radius: 0 0 24px 24px;
  box-shadow: 0 4px 6px rgba(32,33,36,0.18);
  border: 1px solid #e8eaed;
  border-top: 1px solid #e8eaed;
  z-index: 60;
  overflow: hidden;
  max-height: 400px;
  overflow-y: auto;
  padding-bottom: 4px;
}
.suggestions-dropdown::before {
  content: '';
  display: block;
  height: 1px;
  background: #e8eaed;
  margin: 0 14px;
}
.suggestions-dropdown-mobile {
  left: 0;
  right: 0;
  border-radius: 0 0 20px 20px;
  max-height: 320px;
}

.suggestion-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  color: #70757a;
  font-size: 14px;
}

.suggestion-item {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 8px 16px;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  gap: 14px;
  transition: background-color 0.1s;
  line-height: 1.4;
}
.suggestion-item:hover,
.suggestion-active {
  background-color: #f1f3f4;
}

.suggestion-icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9aa0a6;
  font-size: 14px;
}

.suggestion-text {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 6px;
}

.suggestion-name {
  font-size: 14px;
  color: #202124;
  font-weight: 400;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.suggestion-separator {
  color: #bdc1c6;
  font-size: 12px;
  flex-shrink: 0;
}

.suggestion-abbr {
  font-size: 12px;
  color: #70757a;
  font-weight: 400;
  white-space: nowrap;
  flex-shrink: 0;
}

.suggestion-arrow {
  flex-shrink: 0;
  color: #dadce0;
  font-size: 11px;
  opacity: 0;
  transition: opacity 0.15s;
  transform: rotate(-45deg);
}
.suggestion-item:hover .suggestion-arrow,
.suggestion-active .suggestion-arrow {
  opacity: 1;
  color: #70757a;
}

.suggestion-see-all {
  border-top: 1px solid #e8eaed;
  margin-top: 2px;
  padding-top: 10px;
  padding-bottom: 10px;
}
.suggestion-see-all .suggestion-name {
  color: #1a73e8;
  font-size: 13px;
  font-weight: 500;
}
.suggestion-see-all .suggestion-icon {
  color: #1a73e8;
}

.suggestion-name :deep(.highlight-match) {
  font-weight: 700;
  color: #202124;
  background: none;
  padding: 0;
}

/* Scrollbar */
.suggestions-dropdown::-webkit-scrollbar { width: 6px; }
.suggestions-dropdown::-webkit-scrollbar-track { background: transparent; }
.suggestions-dropdown::-webkit-scrollbar-thumb { background: #dadce0; border-radius: 3px; }
.suggestions-dropdown::-webkit-scrollbar-thumb:hover { background: #bdc1c6; }

/* Dropdown Animation */
.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.dropdown-fade-enter-from,
.dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-5px) scale(0.98);
}

.rotate-180 {
  transform: rotate(180deg);
}
.transition-transform {
  transition: transform 0.3s ease-in-out;
}
</style>